import {
  Component,
  input,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import _ from 'lodash';
import { Subject, takeUntil } from 'rxjs';
import { DynamicFormComponent } from 'src/app/core/dynamic-form/dynamic-form.component';
import {
  DynamicFormOptions,
  FieldConfig,
  FieldTypeEnum,
} from 'src/app/core/dynamic-form/dynamicForm.interface';
import { EditHistoryData } from 'src/app/core/edit-history/edit-history';
import { ToastService } from 'src/app/core/toast/toast.service';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { environment } from 'src/environments/environment';
import { StepDto, CreateStep, UpdateStep } from '../step-dto';
import { GestioneStepService } from '../gestione-step.service';
import { EditHistoryModule } from 'src/app/core/edit-history/edit-history.module';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { DynamicFormModule } from 'src/app/core/dynamic-form/dynamic-form.module';
import { MatIconModule } from '@angular/material/icon';
import { slideDownUp } from 'src/app/core/global-app-animations';
import { GestioneEnigmiComponent } from '../../gestione-enigmi/gestione-enigmi.component';
import { GenericDD } from 'src/app/model/generic-data';
import { MissionDto } from '../../gestione-missioni/missioni-dto';
import { GestioneMissioniService } from '../../gestione-missioni/gestione-missioni.service';
import { customBreadCrumbRequest } from 'src/app/core/bread-crumbs/bread-crumbs.component';

@Component({
  selector: 'app-form-step',
  imports: [
    CommonModule,
    DynamicFormModule,
    EditHistoryModule,
    MatButtonModule,
    FormsModule,
    MatIconModule,
    GestioneEnigmiComponent,
  ],
  templateUrl: './form-step.component.html',
  styleUrl: './form-step.component.scss',
  animations: [slideDownUp],
})
export class FormStepComponent implements OnInit, OnDestroy {
  @ViewChild('myForm') myForm!: DynamicFormComponent;

  idMission = input<string>('');
  internalMissionId = signal<string>('');
  currentMission?: MissionDto;

  //DATA FROM API
  public object?: StepDto;

  //ID
  codice: string = '';

  //FORM OPTIONS, DEFINED IN CONSTRUCTOR
  form2Opts: DynamicFormOptions;

  //FOR SHOWING OR HIDING FORM, USEFULL FOR REINITIALIZING
  ready: boolean = false;

  //FOR SHOWING CREATEDBY AND EDITEDBY
  editHistoryData: EditHistoryData | undefined;

  //FORM FIELDS
  regConfig2: FieldConfig[] = [];

  // steps list from mission (per la dipendenza)
  currentSteps: StepDto[] = [];
  currentDataForDD: GenericDD[] = [];

  //NO MEMORY LEAKS FIX
  private theEnd$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private toast: ToastService,
    private stepService: GestioneStepService,
    private localStorageService: LocalStorageService,
    private missionService: GestioneMissioniService
  ) {
    this.form2Opts = {
      buttonAtBottom: true,
      formType: 'add',
    };
  }

  ngOnDestroy(): void {
    this.theEnd$.next();
    this.theEnd$.complete();
  }

  ngOnInit(): void {
    if (this.idMission()) {
      this.internalMissionId.set(this.idMission());
    } else {
      this.internalMissionId.set(this.route.snapshot.paramMap.get('id') || '');
    }

    this.form2Opts = {
      buttonAtBottom: true,
      formType: 'add',
      editOptions: {
        entityName: 'step',
        entityDisplayName: 'Step',
        deleteUrl: `${environment.apiUrl}/steps`,
        deleteMessage: `Stai per eliminare lo step, procedere?`,
        deleteItem: '',
        entityId: '',
        isFem: false,
        redirectUrl: `home/missioni/${this.idMission()}`,
      },
      addOptions: {
        entityName: 'step',
        entityDisplayName: 'Step',
        isFem: false,
        redirectUrl: `home/missioni/${this.idMission()}`,
      },
    };

    this.initializeForm();
    this.loadMissionDetails(this.internalMissionId());
  }

  goBackToMission() {
    const missionId = this.internalMissionId();

    if (!missionId) {
      this.router.navigate(['home/missioni-esplorative']);
      return;
    }

    if (this.currentMission?.isSecondary) {
      this.router.navigate(['home/missioni-esplorative', missionId]);
      return;
    }

    this.router.navigate(['home/missioni', missionId]);
  }

  loadMissionDetails(missionId: string) {
    if (!missionId) return;

    this.missionService
      .read(missionId)
      .pipe(takeUntil(this.theEnd$))
      .subscribe({
        next: (mission: MissionDto) => {
          this.currentMission = mission;

          const redirectUrl = mission.isSecondary
            ? `home/missioni-esplorative/${mission.id}`
            : `home/missioni/${mission.id}`;

          if (this.form2Opts.editOptions) {
            this.form2Opts.editOptions.redirectUrl = redirectUrl;
          }

          if (this.form2Opts.addOptions) {
            this.form2Opts.addOptions.redirectUrl = redirectUrl;
          }
        },
        error: () => {},
      });
  }

  //based on route decides what form to show
  initializeForm() {
    if (this.router.url.includes('new')) {
      this.form2Opts.formType = 'add';
      this.localStorageService.setTitolo('Nuovo Step');
      this.initializeFormFields();
    } else {
      this.form2Opts.formType = 'edit';
      this.initializeEditForm();
    }
  }

  getTheStepsByMission() {
    this.stepService
      .getAllByMission(this.internalMissionId())
      .pipe(takeUntil(this.theEnd$))
      .subscribe({
        next: (data: StepDto[]) => {
          // escludo lo step corrente dall'elenco delle dipendenze
          this.currentSteps = data.filter((s) => s.id !== this.object?.id);

          this.currentDataForDD = [];
          this.currentDataForDD.push({
            codice: 'Nessuna dipendenza',
            id: null,
          });
          for (let s of this.currentSteps) {
            this.currentDataForDD.push({
              codice: s.name,
              id: s.id,
            });
          }

          let i = this.regConfig2.findIndex((x) => x.name == 'stepParent');
          if (i > -1 && this.myForm != undefined) {
            this.regConfig2[i].data = this.currentDataForDD;
            this.regConfig2[i].value = this.object?.dependsOn;
            this.myForm.parsedFields[i] = _.cloneDeep(this.regConfig2[i]);
          }
        },
      });
  }

  initializeFormFields() {
    this.regConfig2 = [
      {
        type: FieldTypeEnum.input,
        name: 'name',
        label: 'Nome',
        isRequired: true,
        width: 'w-full',
        value: this.object ? this.object.name : undefined,
        validations: [
          {
            name: 'required',
            validator: Validators.required,
            message: 'Inserisci un nome',
          },
        ],
      },
      {
        type: FieldTypeEnum.textarea,
        name: 'shortDescription',
        label: 'Breve descrizione',
        isRequired: true,
        width: 'w-full',
        value: this.object ? this.object.shortDescription : undefined,
        validations: [
          {
            name: 'required',
            validator: Validators.required,
            message: 'Inserisci una breve descrizione',
          },
        ],
      },
      {
        type: FieldTypeEnum.textarea,
        name: 'description',
        label: 'Descrizione',
        isRequired: true,
        width: 'w-full',
        value: this.object ? this.object.description : undefined,
        validations: [
          {
            name: 'required',
            validator: Validators.required,
            message: 'Inserisci una descrizione',
          },
        ],
      },
      {
        type: FieldTypeEnum.numberInput,
        name: 'x',
        label: 'Coordinata X',
        isRequired: false,
        width: 'w-1/2',
        value: this.object ? this.object.x : undefined,
      },
      {
        type: FieldTypeEnum.numberInput,
        name: 'y',
        label: 'Coordinata Y',
        isRequired: false,
        width: 'w-1/2',
        value: this.object ? this.object.y : undefined,
      },
      {
        type: FieldTypeEnum.select,
        name: 'stepParent',
        label: 'Step collegato (dipendenza)',
        isRequired: false,
        width: 'w-full',
        value: this.object ? this.object.dependsOn : undefined,
        data: this.currentDataForDD,
      },
    ];

    this.ready = true;

    this.getTheStepsByMission();
  }

  //make api call and get data for edit
  initializeEditForm() {
    this.codice = this.route.snapshot.paramMap.get('id') || '';

    this.stepService
      .read(this.codice)
      .pipe(takeUntil(this.theEnd$))
      .subscribe({
        next: (response: StepDto) => {
          this.object = _.cloneDeep(response);
          this.internalMissionId.set(response.missionFk);
          this.loadMissionDetails(response.missionFk);

          this.localStorageService.setTitolo(`Modifica Step ${response.name}`);

          let req: customBreadCrumbRequest = {
            oldString: response.id,
            newString: response.name,
          };
          this.localStorageService.setCustomBreadCrumb(req);

          if (this.form2Opts.editOptions) {
            this.form2Opts.editOptions.deleteItem = '' + this.object.id;
            this.form2Opts.editOptions.entityId = this.object.id;
          }

          //run this method to display form
          this.initializeFormFields();
        },
        error: () => {},
      });
  }

  //fires when submitting
  submit(event: any) {
    if (this.form2Opts.formType == 'add') {
      this.add(event);
    } else {
      this.edit(event);
    }
  }

  //edit submit
  edit(event: any) {
    let data: UpdateStep = {
      name: event.name,
      shortDescription: event.shortDescription,
      description: event.description,
      missionFk: this.internalMissionId(),
      dependsOn: event.stepParent,
      x: this.toNullableNumber(event.x),
      y: this.toNullableNumber(event.y),
      // preservo l'aggancio alla mappa indoor gia impostato (via mappa-indoor)
      indoorMapFloor: this.object?.indoorMapFloor,
      indoorMapClientFk: this.object?.indoorMapClientFk,
    };

    this.stepService
      .update(this.codice, data)
      .pipe(takeUntil(this.theEnd$))
      .subscribe({
        next: () => {
          this.toast.success('Modifiche salvate');
          this.initializeForm();
        },
        error: () => {},
      });
  }

  //add submit
  add(event: any) {
    let data: CreateStep = {
      name: event.name,
      shortDescription: event.shortDescription,
      description: event.description,
      missionFk: this.internalMissionId(),
      dependsOn: event.stepParent,
      x: this.toNullableNumber(event.x),
      y: this.toNullableNumber(event.y),
    };

    this.stepService
      .create(data)
      .pipe(takeUntil(this.theEnd$))
      .subscribe({
        next: (response: StepDto) => {
          this.toast.success('Step creato');
          this.router.navigate(['home/step', response.id]);
        },
        error: () => {},
      });
  }

  //converte il valore del campo coordinata in number oppure null (campo nullable)
  toNullableNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return isNaN(parsed) ? null : parsed;
  }
}
